import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subject, BehaviorSubject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { WebSocketService } from 'src/app/api/web-socket.service';
import { ExchangeNotifiaction } from 'src/app/models/exchange-notification';
import { WebSocketStatus } from 'src/app/models/wss';
import { TickerService } from 'src/app/api/ticker.service';
import { Theme } from 'src/app/models/theme';

export enum OrderBookPrecision {
  Min = 0,
  Med = 1,
  Max = 2
}

export enum PairType {
  Crypto_Crypto = 'Crypt-Crypto',
  Fiat_Crypto = 'Fiat-Crypto'
}

@Injectable()
export class ExchangeService {
  static readonly DEFAULT_EXCHANGE_PAIR = 'default-exchange-pair';
  static readonly FIAT_CURRENCIES = ['AED', 'SAR', 'USD'];
  DEFAULT_THEME = Theme.Dark;

  private pairSubject = new BehaviorSubject<string>(null);
  private notificationSubject = new Subject<ExchangeNotifiaction>();
  private themeSubject = new BehaviorSubject<Theme>(this.getTheme());
  private orderBookPrecisionSubject = new BehaviorSubject<OrderBookPrecision>(this.getOrderBookPrecision());
  private selectedOrderBookSubject = new Subject<number>();


  constructor(
    private wss: WebSocketService,
    private tickerService: TickerService,
    @Inject(DOCUMENT) private docuemnt: Document
  ) {
    this.loadDefaultPair();
  }

  connect() {
    this.wss.connect(true);

    // Set theme
    this.docuemnt.body.classList.add(this.getTheme());
  }

  disconnect() {
    this.wss.disconnect();

    // remove dark theme from body if it's applied
    this.docuemnt.body.classList.remove(Theme.Dark);
  }

  get pairChanges() {
    return this.pairSubject.asObservable();
  }

  get pairInfoChanges() {
    return this.pairChanges.pipe(
      switchMap(pair => {
        return this.tickerService.getPairInfo(pair || 'BTC-AED');
      })
    );
  }

  get notificationChanges() {
    return this.notificationSubject.asObservable();
  }

  get themeChanges() {
    return this.themeSubject.asObservable();
  }

  get orderBookPrecisionChanges() {
    return this.orderBookPrecisionSubject.asObservable();
  }

  get selectedOrderBookChanges() {
    return this.selectedOrderBookSubject.asObservable();
  }

  get status() {
    return this.wss.statusCahnges;
  }

  setStatus(status: WebSocketStatus) {
    return this.wss.setStatus(status);
  }

  setPair(pair: string) {
    if (!pair) { return; }
    const pairString = pair.toUpperCase();

    window.localStorage.setItem(ExchangeService.DEFAULT_EXCHANGE_PAIR, pair);
    return this.pairSubject.next(pair);
  }

  loadDefaultPair() {
    const pair = window.localStorage.getItem(ExchangeService.DEFAULT_EXCHANGE_PAIR);

    if (pair) {
      this.setPair(pair);
    } else {
      this.tickerService.getAllSorted().subscribe(response => {
        if (response[0]?.pairInfo.pair) {
          this.setPair(response[0].pairInfo.pair);
        } else {
          this.setPair('BTC-ETH');
        }
      }, err => {
        this.setPair('BTC-ETH');
      });
    }
  }

  getPairType(pair: string) {
    const quoteCurrency = pair.split('-')?.[1];

    if(ExchangeService.FIAT_CURRENCIES.includes(quoteCurrency)) {
      return PairType.Fiat_Crypto;
    }

    return PairType.Crypto_Crypto;
  }

  setNotifiaction(notifiaction: ExchangeNotifiaction) {
    this.notificationSubject.next(notifiaction);
  }

  getTheme() {
    const theme = window.localStorage.getItem('theme') as Theme;
    return theme ? theme : this.DEFAULT_THEME;
  }

  setTheme(theme: Theme) {
    if (!theme) { return; }

    if (!Object.values(Theme).includes(theme)) {
      return
    }

    this.docuemnt.body.classList.remove(this.getTheme());
    window.localStorage.setItem('theme', theme);
    this.docuemnt.body.classList.add(theme);
    this.themeSubject.next(theme);
  }

  clearTheme() {
    this.docuemnt.body.classList.remove(this.getTheme());
  }

  getOrderBookPrecision() {
    let precision =  parseInt(window.localStorage.getItem('orderBookPrecision'), 10) as OrderBookPrecision;
    if ((precision !== 0 && !precision) || precision < OrderBookPrecision.Min || precision > OrderBookPrecision.Max) {
      precision = OrderBookPrecision.Max;
    }

    return precision;
  }

  setOrderBookPrecision(precision: OrderBookPrecision) {
    if ((precision !== 0 && !precision) || precision < OrderBookPrecision.Min || precision > OrderBookPrecision.Max) {
      return;
    }

    window.localStorage.setItem('orderBookPrecision', precision.toString());
    this.orderBookPrecisionSubject.next(precision);
  }

  setSelectedOrderBook(val: number) {
    this.selectedOrderBookSubject.next(val);
  }
}

